import { Data, Effect } from 'effect';
import { extractStudioPageTexts } from '@okrapdf/pdf-studio-plugin/studio';
import { getClerkToken } from '@/api/clerkTokenBridge';
import { promptLogin } from '@/api/loginRequired';
import { catalogueFor, type IntentTool } from './studio-intent';

/**
 * Browser side of the Okra Agent workspace (W.4.106): put the open PDF in the
 * Agent's workspace, send one chat turn and read its answer as it streams, and
 * load or delete the Agent's chats about a document (W.4.132).
 *
 * Every call is a same-origin request to the web Worker (`/api/agent/studio/*`)
 * with the Clerk session as a Bearer token and no cookies. Nothing here runs
 * when a PDF is opened: the upload happens on send. No React, so the turn
 * keeps going when the Agent panel unmounts.
 */

const BASE_PATH = '/api/agent/studio';

// Mirrors of the endpoint's gates: outside them the server answers 400 or 413.
const MAX_DOCUMENT_BYTES = 20 * 1024 * 1024;
const MAX_TEXT_PAGES = 500;
/** The `meta` field as it travels. Decimal, so it is under the 2 MB gate however that is counted. */
const MAX_META_BYTES = 2_000_000;
/** Room in `meta` for the name, the page count and the JSON around the pages. */
const META_ENVELOPE_BYTES = 1_024;
const MAX_DOCUMENT_NAME_CHARS = 120;
const MAX_MESSAGE_CHARS = 2_000;
const MAX_LABEL_CHARS = 120;
const MAX_REASON_CHARS = 200;
const MAX_ERROR_CHARS = 200;
const MAX_PREVIEW_CHARS = 120;
const FALLBACK_DOCUMENT_NAME = 'document.pdf';
/** The Agent takes one upload or turn at a time; it is free again about this soon. */
const BUSY_RETRY_MS = 1_200;

/** Shown when a stream stops without saying how it ended. */
export const STREAM_FAILED_MESSAGE = 'Okra Agent could not finish the answer. Try again.';

export type StudioAgentFailure =
  | 'login_required'
  | 'rate_limited'
  | 'ai_unavailable'
  | 'invalid_request'
  | 'ai_failed'
  | 'network'
  /** The caller's own signal stopped the request. */
  | 'aborted';

/** Which limit a `429` is: the per-minute one, or the cap that resets at 00:00 UTC. */
export type StudioAgentLimitScope = 'minute' | 'daily';

/**
 * Why a request to the Agent did not go through. Carries the reason token, the
 * status, `Retry-After` and the limit's scope only: never the server's
 * message, the prompt or the file name.
 */
export class StudioAgentError extends Data.TaggedError('StudioAgentError')<{
  readonly reason: StudioAgentFailure;
  readonly status: number | null;
  /** `Retry-After` in seconds, when the answer carried one. */
  readonly retryAfterSeconds: number | null;
  /** Which limit refused it, when a `429` said. An older Worker does not say. */
  readonly scope: StudioAgentLimitScope | null;
}> {}

function failure(
  reason: StudioAgentFailure,
  detail?: {
    status?: number;
    retryAfterSeconds?: number | null;
    scope?: StudioAgentLimitScope | null;
  },
): StudioAgentError {
  return new StudioAgentError({
    reason,
    status: detail?.status ?? null,
    retryAfterSeconds: detail?.retryAfterSeconds ?? null,
    scope: detail?.scope ?? null,
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The endpoint's `error` token, when it is one this client knows. */
function serverReason(code: unknown): StudioAgentFailure | null {
  switch (code) {
    case 'login_required':
    case 'rate_limited':
    case 'ai_unavailable':
    case 'ai_failed':
    case 'invalid_request':
      return code;
    default:
      return null;
  }
}

/** The answer's `scope`, when it is one this client knows. */
function limitScope(scope: unknown): StudioAgentLimitScope | null {
  return scope === 'minute' || scope === 'daily' ? scope : null;
}

function statusReason(status: number): StudioAgentFailure {
  switch (status) {
    case 400:
    case 413:
      return 'invalid_request';
    case 401:
      return 'login_required';
    case 429:
      return 'rate_limited';
    // No such route: a Worker from before the workspace Agent is answering.
    case 404:
    case 405:
    case 503:
      return 'ai_unavailable';
    default:
      return 'ai_failed';
  }
}

/** `Retry-After` as seconds from now; it may be a number or an HTTP date. */
function retryAfterSeconds(response: Response): number | null {
  const header = response.headers.get('Retry-After');
  if (!header) return null;
  const seconds = Number(header);
  if (Number.isFinite(seconds)) return Math.max(0, Math.ceil(seconds));
  const date = Date.parse(header);
  return Number.isNaN(date) ? null : Math.max(0, Math.ceil((date - Date.now()) / 1000));
}

interface AgentRequest {
  method: 'GET' | 'POST' | 'DELETE';
  accept: string;
  /** Built after the session is known, so a signed-out visitor costs no work. */
  body?: () => Promise<BodyInit>;
  /** Set for a JSON body. A form sets its own type, boundary included. */
  contentType?: string;
  signal?: AbortSignal;
  /** Open sign-up on `401 login_required`. Off for calls the visitor did not make. */
  promptLogin?: boolean;
}

/**
 * One request to the Agent. Succeeds with a 2xx response; anything else fails
 * with a `StudioAgentError`. Signed out is decided here, without a request.
 */
const requestEffect = Effect.fn('web.studioAgent.request')(function* (
  path: string,
  request: AgentRequest,
): Effect.fn.Return<Response, StudioAgentError> {
  const aborted = () => request.signal?.aborted === true;
  const token = yield* Effect.tryPromise({
    try: () => getClerkToken(),
    catch: () => failure('network'),
  });
  if (!token) return yield* Effect.fail(failure('login_required'));

  const build = request.body;
  const body = build
    ? yield* Effect.tryPromise({
        try: build,
        catch: (cause) =>
          cause instanceof StudioAgentError ? cause : failure(aborted() ? 'aborted' : 'ai_failed'),
      })
    : undefined;

  const response = yield* Effect.tryPromise({
    try: () =>
      fetch(`${BASE_PATH}${path}`, {
        method: request.method,
        signal: request.signal,
        credentials: 'omit',
        cache: 'no-store',
        headers: {
          Accept: request.accept,
          Authorization: `Bearer ${token}`,
          ...(request.contentType ? { 'Content-Type': request.contentType } : {}),
        },
        body,
      }),
    catch: () => failure(aborted() ? 'aborted' : 'network'),
  });
  if (response.ok) return response;

  const answer: unknown = yield* Effect.promise(() => response.json().catch(() => null));
  const reason =
    serverReason(isRecord(answer) ? answer.error : null) ?? statusReason(response.status);
  if (reason === 'login_required' && request.promptLogin) {
    // The Studio sits behind the sign-in gate, so this is an expired session.
    yield* Effect.sync(() => {
      try {
        promptLogin({ surface: 'studio', tool: 'okra-agent' }, 'server_401');
      } catch {
        /* analytics unavailable — the caller still shows its own line */
      }
    });
  }
  return yield* Effect.fail(
    failure(reason, {
      status: response.status,
      retryAfterSeconds: retryAfterSeconds(response),
      scope: reason === 'rate_limited' && isRecord(answer) ? limitScope(answer.scope) : null,
    }),
  );
});

function send(path: string, request: AgentRequest): Promise<Response> {
  return Effect.runPromise(requestEffect(path, request));
}

/** Whatever a call threw, as the typed error. */
function asFailure(error: unknown, signal?: AbortSignal): StudioAgentError {
  if (error instanceof StudioAgentError) return error;
  return failure(signal?.aborted ? 'aborted' : 'ai_failed');
}

/** Wait `ms`, unless the caller stops first. */
function pause(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(failure('aborted'));
      return;
    }
    const stop = () => {
      clearTimeout(timer);
      reject(failure('aborted'));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', stop);
      resolve();
    }, ms);
    signal?.addEventListener('abort', stop, { once: true });
  });
}

// ── Document ─────────────────────────────────────────────────────────────────

export interface AgentDocument {
  name: string;
  bytes: Uint8Array;
  pageCount: number;
  /** The document's key (`ActiveStudioDocument.documentKey`), sent with the upload. */
  key?: string | null;
}

export type SyncDocumentResult =
  | {
      ok: true;
      /** Pages whose text is in the workspace. */
      textPages: number;
      /** The workspace holds fewer text pages than the PDF has. */
      truncated: boolean;
      /** This revision was already there: nothing was uploaded. */
      skipped: boolean;
    }
  | {
      ok: false;
      reason: 'too_large' | StudioAgentFailure;
      retryAfterSeconds: number | null;
      /** Which limit refused the upload, when `reason` is `rate_limited` and the answer said. */
      scope: StudioAgentLimitScope | null;
    };

/**
 * The upload that last succeeded in this page session. The Agent keeps one
 * document, so one entry is the whole memo. Dropped when the account changes,
 * and when the Agent says it holds something else. Deleting a chat leaves the
 * PDF in the workspace, so it keeps the memo; Delete all does not.
 */
let synced: {
  revisionKey: string;
  name: string;
  /** The document key the upload named, if any. */
  documentKey: string | null;
  textPages: number;
  truncated: boolean;
} | null = null;

function revisionKey(doc: AgentDocument, revision: number | string): string {
  return JSON.stringify([doc.name, doc.bytes.byteLength, doc.key ?? null, revision]);
}

/** A file name as the workspace records it. */
function workspaceName(name: string): string {
  return (name.trim() || FALLBACK_DOCUMENT_NAME).slice(0, MAX_DOCUMENT_NAME_CHARS);
}

/** Whether this revision is already in the workspace, so sending it costs nothing. */
export function isDocumentSynced(doc: AgentDocument, revision: number | string): boolean {
  return synced?.revisionKey === revisionKey(doc, revision);
}

/** Forget the last upload: the next send puts the open PDF in the workspace again. */
export function resetSyncedDocument(): void {
  synced = null;
}

const encoder = new TextEncoder();

/**
 * The leading pages that fit the endpoint's caps. Pages stay in order and are
 * never skipped: page 3 of the list is page 3 of the PDF, so the first page
 * that does not fit ends the list.
 */
function fitPages(pages: string[]): string[] {
  const kept: string[] = [];
  let size = 0;
  for (const page of pages.slice(0, MAX_TEXT_PAGES)) {
    // Counted as it travels: the JSON string, escapes included, and its comma.
    size += encoder.encode(JSON.stringify(page)).length + 1;
    if (size > MAX_META_BYTES - META_ENVELOPE_BYTES) break;
    kept.push(page);
  }
  return kept;
}

async function documentForm(
  doc: AgentDocument,
  signal?: AbortSignal,
): Promise<{ form: FormData; pages: number }> {
  let pages: string[];
  try {
    pages = await extractStudioPageTexts(doc.bytes, { maxPages: MAX_TEXT_PAGES, signal });
  } catch {
    if (signal?.aborted) throw failure('aborted');
    // A PDF whose text cannot be read still goes in: the Agent sees pages with
    // no text and says to run OCR, which is the right answer for it.
    pages = [];
  }
  const kept = fitPages(pages);

  const form = new FormData();
  // A File, so the part carries the real file name; the workspace's label is `meta.name`.
  form.append(
    'file',
    new File([doc.bytes as BlobPart], doc.name.trim() || FALLBACK_DOCUMENT_NAME, {
      type: 'application/pdf',
    }),
  );
  form.append(
    'meta',
    JSON.stringify({
      name: workspaceName(doc.name),
      pageCount: Math.max(1, Math.trunc(doc.pageCount) || 1),
      pages: kept,
      ...(doc.key ? { key: doc.key } : {}),
    }),
  );
  return { form, pages: kept.length };
}

/**
 * Put the open PDF and its page text in the Agent's workspace. Uploads once
 * per document revision; a PDF over 20 MB is not uploaded at all. An Agent
 * that is busy (`409`) is asked once more after a moment. Never throws: every
 * way it can go wrong is a `reason`.
 */
export async function syncDocument(
  doc: AgentDocument,
  revision: number | string,
  signal?: AbortSignal,
): Promise<SyncDocumentResult> {
  if (doc.bytes.byteLength > MAX_DOCUMENT_BYTES) {
    return { ok: false, reason: 'too_large', retryAfterSeconds: null, scope: null };
  }
  const key = revisionKey(doc, revision);
  if (synced?.revisionKey === key) {
    return { ok: true, textPages: synced.textPages, truncated: synced.truncated, skipped: true };
  }

  let form: FormData | null = null;
  let sentPages = 0;
  const upload = () =>
    send('/document', {
      method: 'POST',
      accept: 'application/json',
      signal,
      promptLogin: true,
      body: async () => {
        // Built once: the second try sends the same form without reading the pages again.
        if (!form) {
          const built = await documentForm(doc, signal);
          form = built.form;
          sentPages = built.pages;
        }
        return form;
      },
    });
  try {
    let response: Response;
    try {
      response = await upload();
    } catch (error) {
      // Busy with another upload or a turn (another tab, or one just stopped here).
      if (asFailure(error, signal).status !== 409) throw error;
      await pause(BUSY_RETRY_MS, signal);
      response = await upload();
    }
    const answer: unknown = await response.json().catch(() => null);
    const stored = isRecord(answer) ? answer : {};
    const textPages =
      typeof stored.textPages === 'number' && Number.isFinite(stored.textPages)
        ? Math.max(0, Math.trunc(stored.textPages))
        : sentPages;
    const truncated = stored.truncated === true || sentPages < Math.trunc(doc.pageCount);
    synced = {
      revisionKey: key,
      name: workspaceName(doc.name),
      documentKey: doc.key ?? null,
      textPages,
      truncated,
    };
    return { ok: true, textPages, truncated, skipped: false };
  } catch (error) {
    const failed = asFailure(error, signal);
    return {
      ok: false,
      // The server's own size gate counts the whole upload, the page text included.
      reason: failed.status === 413 ? 'too_large' : failed.reason,
      retryAfterSeconds: failed.retryAfterSeconds,
      scope: failed.scope,
    };
  }
}

// ── Chat ─────────────────────────────────────────────────────────────────────

export type AgentActivityState = 'start' | 'done' | 'error';

export interface AgentActivity {
  id: string;
  /** The workspace tool the Agent called: `read`, `ls`, `find` or `grep`. */
  tool: string;
  label: string;
  state: AgentActivityState;
}

export interface AgentOpenTool {
  toolId: string;
  reason: string;
}

export interface StudioChatRequest {
  message: string;
  tools: IntentTool[];
  activeToolId: string | null;
  /**
   * The open PDF, when it is in the workspace for this turn. `null` or absent
   * tells the Agent the visitor has no document it can read right now, so it
   * does not answer from one an earlier turn left there. With its key the
   * Agent tells it apart from another PDF of the same name (W.4.132).
   */
  document?: { name: string; key?: string | null } | null;
  /**
   * The chat the turn belongs to (W.4.132): `<document key>.<n>`, or with no
   * PDF open the page's own chat, whose key is random.
   */
  thread?: string | null;
}

export interface StudioChatHandlers {
  onText: (delta: string) => void;
  onActivity: (activity: AgentActivity) => void;
  onOpenTool: (openTool: AgentOpenTool) => void;
  onDone: (messageId: string) => void;
  onError: (message: string) => void;
}

/**
 * Server-sent events, fed text as it arrives. Lines may end in LF, CRLF or CR
 * and may be split anywhere between chunks. `end` also delivers a last event
 * that has no blank line after it: the Agent's `done` must not be lost to a
 * connection that closed one byte early.
 */
function sseParser(onEvent: (event: string, data: string) => void) {
  let buffer = '';
  let event = '';
  let data: string[] = [];

  const line = (text: string) => {
    if (text === '') {
      if (data.length > 0) onEvent(event || 'message', data.join('\n'));
      event = '';
      data = [];
      return;
    }
    if (text.startsWith(':')) return;
    const colon = text.indexOf(':');
    const field = colon === -1 ? text : text.slice(0, colon);
    let value = colon === -1 ? '' : text.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') event = value;
    else if (field === 'data') data.push(value);
  };

  return {
    push(chunk: string) {
      buffer += chunk;
      let start = 0;
      for (;;) {
        const rest = buffer.slice(start).search(/[\r\n]/);
        if (rest === -1) break;
        const end = start + rest;
        // A CR at the very end may be the first half of a CRLF still on its way.
        if (buffer[end] === '\r' && end === buffer.length - 1) break;
        line(buffer.slice(start, end));
        start = end + (buffer[end] === '\r' && buffer[end + 1] === '\n' ? 2 : 1);
      }
      buffer = buffer.slice(start);
    },
    end() {
      if (buffer !== '') line(buffer.endsWith('\r') ? buffer.slice(0, -1) : buffer);
      buffer = '';
      line('');
    },
  };
}

function readActivity(data: Record<string, unknown>): AgentActivity | null {
  const { id, tool, label, state } = data;
  if (typeof id !== 'string' || id === '' || typeof label !== 'string' || !label.trim()) return null;
  if (state !== 'start' && state !== 'done' && state !== 'error') return null;
  return {
    id,
    tool: typeof tool === 'string' ? tool : '',
    label: label.trim().slice(0, MAX_LABEL_CHARS),
    state,
  };
}

/**
 * Send one message and read the answer as it streams.
 *
 * Rejects with a `StudioAgentError` when the turn never started (signed out,
 * refused, unreachable) or when `signal` stopped it. Once the stream is open
 * it ends with exactly one `onDone` or `onError` and resolves. A tool id that
 * was not in `tools` never reaches `onOpenTool`: the answer drives navigation.
 */
export async function streamChat(
  request: StudioChatRequest,
  handlers: StudioChatHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const message = request.message.trim().slice(0, MAX_MESSAGE_CHARS);
  const tools = catalogueFor(request.tools);
  // The endpoint refuses both; skip the request that could only answer 400.
  if (!message || tools.length === 0) throw failure('invalid_request');
  const known = new Set(tools.map((tool) => tool.id));

  const response = await send('/chat', {
    method: 'POST',
    accept: 'text/event-stream',
    contentType: 'application/json',
    signal,
    promptLogin: true,
    body: async () =>
      JSON.stringify({
        message,
        tools,
        // The open tool may be one the catalogue leaves out (the Agent panel itself).
        activeToolId:
          request.activeToolId !== null && known.has(request.activeToolId)
            ? request.activeToolId
            : null,
        document: request.document
          ? {
              name: workspaceName(request.document.name),
              ...(request.document.key ? { key: request.document.key } : {}),
            }
          : null,
        ...(request.thread ? { thread: request.thread } : {}),
      }),
  });
  // A 200 that is not a stream is a deploy without the route (the app shell
  // answering in its place), not an answer.
  const type = response.headers.get('Content-Type') ?? '';
  if (!response.body || !type.includes('text/event-stream')) throw failure('ai_unavailable');

  let ended = false;
  const parser = sseParser((event, raw) => {
    if (ended) return;
    let data: unknown;
    try {
      data = JSON.parse(raw);
    } catch {
      return;
    }
    if (!isRecord(data)) return;
    switch (event) {
      case 'text':
        if (typeof data.delta === 'string' && data.delta !== '') handlers.onText(data.delta);
        return;
      case 'activity': {
        const activity = readActivity(data);
        if (activity) handlers.onActivity(activity);
        return;
      }
      case 'open_tool':
        if (typeof data.toolId === 'string' && known.has(data.toolId)) {
          handlers.onOpenTool({
            toolId: data.toolId,
            reason: typeof data.reason === 'string' ? data.reason.slice(0, MAX_REASON_CHARS) : '',
          });
        }
        return;
      case 'done':
        ended = true;
        handlers.onDone(typeof data.messageId === 'string' ? data.messageId : '');
        return;
      case 'error':
        ended = true;
        handlers.onError(
          typeof data.message === 'string' && data.message.trim()
            ? data.message.trim().slice(0, MAX_ERROR_CHARS)
            : STREAM_FAILED_MESSAGE,
        );
        return;
      default:
        return;
    }
  });

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  try {
    while (!ended) {
      const { done, value } = await reader.read();
      if (signal?.aborted) throw failure('aborted');
      if (value) parser.push(decoder.decode(value, { stream: true }));
      if (done) {
        parser.push(decoder.decode());
        parser.end();
        break;
      }
    }
  } catch (error) {
    if (signal?.aborted) throw failure('aborted');
    if (error instanceof StudioAgentError) throw error;
    // The connection dropped mid-answer: what streamed so far stays on screen.
    if (!ended) {
      ended = true;
      handlers.onError(STREAM_FAILED_MESSAGE);
    }
    return;
  } finally {
    // After `done` or `error` nothing more is read; let the connection go.
    void reader.cancel().catch(() => undefined);
  }
  if (!ended) handlers.onError(STREAM_FAILED_MESSAGE);
}

// ── State ────────────────────────────────────────────────────────────────────

export interface StudioAgentMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  openTools: AgentOpenTool[];
}

/** One of the Agent's chats about a document, as it lists them. */
export interface StudioAgentThread {
  id: string;
  /** Its first question, cut short: the visitor's words, so the panel masks it in replay. */
  preview: string;
  /** Epoch milliseconds of its last message. */
  updatedAt: number;
}

export interface StudioAgentChats {
  /** The workspace's PDF while it is this document; null otherwise. */
  document: { name: string; pageCount: number; textPages: number } | null;
  /** The Agent's chats about the document, the most recent first. */
  threads: StudioAgentThread[];
  /** The chat `messages` belong to: the one asked for, or the most recent. Null with none. */
  thread: string | null;
  messages: StudioAgentMessage[];
}

function readMessage(value: unknown): StudioAgentMessage | null {
  if (!isRecord(value)) return null;
  const { id, role, text, openTools } = value;
  if (typeof id !== 'string' || typeof text !== 'string') return null;
  if (role !== 'user' && role !== 'assistant') return null;
  const tools: AgentOpenTool[] = [];
  if (Array.isArray(openTools)) {
    for (const entry of openTools) {
      if (!isRecord(entry) || typeof entry.toolId !== 'string') continue;
      tools.push({
        toolId: entry.toolId,
        reason: typeof entry.reason === 'string' ? entry.reason.slice(0, MAX_REASON_CHARS) : '',
      });
    }
  }
  return { id, role, text, openTools: tools };
}

function readThread(value: unknown, documentKey: string): StudioAgentThread | null {
  if (!isRecord(value) || typeof value.id !== 'string') return null;
  // Only a chat about this document: the panel shows it beside this PDF.
  if (!value.id.startsWith(`${documentKey}.`)) return null;
  return {
    id: value.id,
    preview: typeof value.preview === 'string' ? value.preview.slice(0, MAX_PREVIEW_CHARS) : '',
    updatedAt: typeof value.updatedAt === 'number' ? value.updatedAt : 0,
  };
}

/**
 * The contract of `GET /state?document=`. An Agent from before chats ignores
 * the query and answers with its one conversation and no `threads`. That
 * conversation is not this document's, so none of it is shown: the document
 * reads as having no chat yet, and the Agent still answers.
 */
function readChats(value: unknown, documentKey: string): StudioAgentChats | null {
  if (!isRecord(value) || !Array.isArray(value.messages)) return null;
  const chats = Array.isArray(value.threads) ? value.threads : null;
  const messages: StudioAgentMessage[] = [];
  for (const entry of chats ? value.messages : []) {
    const message = readMessage(entry);
    if (message) messages.push(message);
  }
  const threads: StudioAgentThread[] = [];
  for (const entry of chats ?? []) {
    const thread = readThread(entry, documentKey);
    if (thread) threads.push(thread);
  }
  const stored = value.document;
  const document =
    isRecord(stored) && typeof stored.name === 'string'
      ? {
          name: stored.name,
          pageCount: typeof stored.pageCount === 'number' ? stored.pageCount : 0,
          textPages: typeof stored.textPages === 'number' ? stored.textPages : 0,
        }
      : null;
  const thread =
    chats && typeof value.thread === 'string' && value.thread.startsWith(`${documentKey}.`)
      ? value.thread
      : null;
  // An Agent from before chats keeps no key, so its document is not known to be this one.
  return { document: chats ? document : null, threads, thread, messages };
}

/**
 * The Agent's chats about one document and the messages of one of them: the
 * one named, or the most recent. Rejects with a `StudioAgentError`; never
 * opens sign-up, because the visitor did not ask for this call.
 */
export async function loadChats(
  query: { document: string; thread?: string | null },
  signal?: AbortSignal,
): Promise<StudioAgentChats> {
  const params = new URLSearchParams({ document: query.document });
  if (query.thread) params.set('thread', query.thread);
  let state: StudioAgentChats | null;
  try {
    const response = await send(`/state?${params}`, {
      method: 'GET',
      accept: 'application/json',
      signal,
    });
    state = readChats(await response.json().catch(() => null), query.document);
  } catch (error) {
    throw asFailure(error, signal);
  }
  // A body that is not the contract is a deploy without the route.
  if (!state) throw failure('ai_unavailable');
  // Another tab may have cleared the workspace or put a different PDF there.
  if (synced?.documentKey === query.document && state.document?.name !== synced.name) {
    synced = null;
  }
  return state;
}

/**
 * Delete one chat at the Agent. The other chats and the PDF in the workspace
 * stay; everything goes with `clearState`, or once the Agent has had no
 * request for 7 days.
 */
export async function clearChat(thread: string, signal?: AbortSignal): Promise<void> {
  try {
    await send(`/state?${new URLSearchParams({ thread })}`, {
      method: 'DELETE',
      accept: 'application/json',
      signal,
    });
  } catch (error) {
    throw asFailure(error, signal);
  }
}

/**
 * Delete everything the Agent holds for this account: every chat, about every
 * PDF, and the PDF in its workspace (`DELETE /state` with no query). The upload
 * memo is the caller's to drop once this succeeds (`resetSyncedDocument`).
 */
export async function clearState(signal?: AbortSignal): Promise<void> {
  try {
    await send('/state', { method: 'DELETE', accept: 'application/json', signal });
  } catch (error) {
    throw asFailure(error, signal);
  }
}
